"use client"

// src/JobsComponent.tsx
import { createContext, useContext } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// src/components/jobs/JobsComponent.tsx
import { useState as useState2 } from "react";
import { format, subMonths } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@umd-dit-ai-workspace/shared-ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@umd-dit-ai-workspace/shared-ui";

// src/components/jobs/MonthlyStatsView.tsx
import { Card, CardContent, CardHeader, CardTitle } from "@umd-dit-ai-workspace/shared-ui";

// src/hooks/useJobs.ts
import { useQuery } from "@tanstack/react-query";

// src/api/jobs-api.ts
var createJobsApi = (api_instance) => ({
  // Get job function
  getJob: async (job_id) => {
    const response = await api_instance.get(`/jobs/${job_id}`);
    return response.data;
  },
  // Get organization-level job
  getOrgJob: async (org_id, job_id) => {
    const response = await api_instance.get(`/org/${org_id}/jobs/${job_id}`);
    return response.data;
  },
  // Get workspace-level job
  getWorkspaceJob: async (org_id, ws_id, job_id) => {
    const response = await api_instance.get(`/org/${org_id}/ws/${ws_id}/jobs/${job_id}`);
    return response.data;
  },
  // Utility function to get job based on context
  getJobByContext: async ({ org_id, ws_id }, job_id) => {
    if (ws_id && org_id) {
      const response = await api_instance.get(`/org/${org_id}/ws/${ws_id}/jobs/${job_id}`);
      return response.data;
    } else if (org_id) {
      const response = await api_instance.get(`/org/${org_id}/jobs/${job_id}`);
      return response.data;
    } else {
      const response = await api_instance.get(`/jobs/${job_id}`);
      return response.data;
    }
  },
  // Get monthly stats
  getMonthlyStats: async (month, process_type) => {
    const url = process_type ? `/jobs/stats/monthly/${month}?process_type=${process_type}` : `/jobs/stats/monthly/${month}`;
    const response = await api_instance.get(url);
    return response.data;
  },
  // Get monthly stats for organization
  getMonthlyOrgStats: async (org_id, month, process_type) => {
    const url = process_type ? `/org/${org_id}/jobs/stats/monthly/${month}?process_type=${process_type}` : `/org/${org_id}/jobs/stats/monthly/${month}`;
    const response = await api_instance.get(url);
    return response.data;
  },
  // Get monthly stats for workspace
  getMonthlyWorkspaceStats: async (org_id, ws_id, month, process_type) => {
    const url = process_type ? `/org/${org_id}/ws/${ws_id}/jobs/stats/monthly/${month}?process_type=${process_type}` : `/org/${org_id}/ws/${ws_id}/jobs/stats/monthly/${month}`;
    const response = await api_instance.get(url);
    return response.data;
  },
  // Utility function to get monthly stats based on context
  getMonthlyStatsByContext: async ({ org_id, ws_id }, month, process_type) => {
    if (ws_id && org_id) {
      return createJobsApi(api_instance).getMonthlyWorkspaceStats(org_id, ws_id, month, process_type);
    } else if (org_id) {
      return createJobsApi(api_instance).getMonthlyOrgStats(org_id, month, process_type);
    } else {
      return createJobsApi(api_instance).getMonthlyStats(month, process_type);
    }
  },
  // Get recent jobs functions
  getRecentJobs: async (limit = 500, month) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (month) params.append("month", month);
    const response = await api_instance.get(`/jobs/recent?${params.toString()}`);
    return response.data;
  },
  getRecentOrgJobs: async (org_id, limit = 500, month) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (month) params.append("month", month);
    const response = await api_instance.get(`/org/${org_id}/jobs/recent?${params.toString()}`);
    return response.data;
  },
  getRecentWorkspaceJobs: async (org_id, ws_id, limit = 500, month) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (month) params.append("month", month);
    const response = await api_instance.get(`/org/${org_id}/ws/${ws_id}/jobs/recent?${params.toString()}`);
    return response.data;
  },
  // Utility function that chooses the right API call based on context
  getRecentJobsByContext: async ({ org_id, ws_id }, limit = 500, month) => {
    if (ws_id && org_id) {
      return createJobsApi(api_instance).getRecentWorkspaceJobs(org_id, ws_id, limit, month);
    } else if (org_id) {
      return createJobsApi(api_instance).getRecentOrgJobs(org_id, limit, month);
    } else {
      return createJobsApi(api_instance).getRecentJobs(limit, month);
    }
  }
});

// src/hooks/useJobs.ts
var jobsApiCache = /* @__PURE__ */ new WeakMap();
var useMonthlyStats = (month, org_id, ws_id) => {
  const { apiInstance } = useApi();
  if (!jobsApiCache.has(apiInstance)) {
    jobsApiCache.set(apiInstance, createJobsApi(apiInstance));
  }
  const jobsApi2 = jobsApiCache.get(apiInstance);
  return useQuery({
    queryKey: ["jobStats", month, org_id, ws_id],
    queryFn: () => jobsApi2.getMonthlyStatsByContext({ org_id, ws_id }, month)
  });
};
var useRecentJobs = (org_id, ws_id, month) => {
  const { apiInstance } = useApi();
  if (!jobsApiCache.has(apiInstance)) {
    jobsApiCache.set(apiInstance, createJobsApi(apiInstance));
  }
  const jobsApi2 = jobsApiCache.get(apiInstance);
  const context = { org_id, ws_id };
  return useQuery({
    queryKey: ["recentJobs", org_id, ws_id, month],
    queryFn: () => jobsApi2.getRecentJobsByContext(context, 500, month),
    refetchInterval: 3e4
  });
};

// src/components/jobs/MonthlyStatsView.tsx
import { jsx, jsxs } from "react/jsx-runtime";
function isErrorWithMessage(error) {
  return typeof error === "object" && error !== null && "message" in error && typeof error.message === "string";
}
function getErrorMessage(error) {
  if (isErrorWithMessage(error)) {
    return error.message;
  }
  return "Unknown error";
}
var getJobTypeName = (type) => {
  switch (type) {
    case "sync_bills":
      return "Sync Jobs";
    case "process_bills":
      return "Process Jobs";
    case "monitor_bills":
      return "Monitor Jobs";
    default:
      return type;
  }
};
function MonthlyStatsView({ month, org_id, ws_id }) {
  const { data: stats, isError, error } = useMonthlyStats(month, org_id, ws_id);
  if (isError) {
    console.error("Error fetching monthly stats:", error);
    return /* @__PURE__ */ jsx("div", { className: "p-4 bg-red-50 border border-red-200 rounded-md", children: /* @__PURE__ */ jsxs("p", { className: "text-red-600", children: [
      "Error loading stats: ",
      getErrorMessage(error)
    ] }) });
  }
  if (!stats) return /* @__PURE__ */ jsx("div", { className: "p-4", children: "Loading stats..." });
  return /* @__PURE__ */ jsx("div", { className: "grid gap-4 md:grid-cols-3", children: Object.entries(stats).map(([type, data]) => /* @__PURE__ */ jsxs(Card, { children: [
    /* @__PURE__ */ jsx(CardHeader, { children: /* @__PURE__ */ jsx(CardTitle, { children: getJobTypeName(type) }) }),
    /* @__PURE__ */ jsx(CardContent, { children: /* @__PURE__ */ jsxs("dl", { className: "grid grid-cols-2 gap-2", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("dt", { className: "text-sm text-muted-foreground", children: "Success Rate" }),
        /* @__PURE__ */ jsxs("dd", { children: [
          data.success_rate.toFixed(1),
          "%"
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("dt", { className: "text-sm text-muted-foreground", children: "Avg Wait Time" }),
        /* @__PURE__ */ jsxs("dd", { children: [
          (data.avg_wait_duration_ms / 1e3).toFixed(1),
          "s"
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("dt", { className: "text-sm text-muted-foreground", children: "Avg Run Time" }),
        /* @__PURE__ */ jsxs("dd", { children: [
          (data.avg_run_duration_ms / 1e3).toFixed(1),
          "s"
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("dt", { className: "text-sm text-muted-foreground", children: "Total/Completed" }),
        /* @__PURE__ */ jsxs("dd", { children: [
          data.completed_jobs,
          "/",
          data.total_jobs
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("dt", { className: "text-sm text-muted-foreground", children: "Errors" }),
        /* @__PURE__ */ jsx("dd", { children: data.error_count })
      ] })
    ] }) })
  ] }, type)) });
}

// src/components/jobs/RecentJobsTable.tsx
import { DataTable } from "@umd-dit-ai-workspace/shared-ui";

// src/utils/date.ts
function formatToEastern(dateStr) {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    console.error("Invalid date string:", dateStr);
    return "Invalid Date";
  }
  return date.toLocaleString("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true
  });
}

// src/components/jobs/columns.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var getJobTypeName2 = (processName) => {
  switch (processName) {
    case "sync_bills":
      return "Sync";
    case "process_bills":
      return "Process";
    case "monitor_bills":
      return "Monitor";
    case "pipeline_execution":
      return "Pipeline";
    default:
      return processName;
  }
};
var formatMetadataValue = (value) => {
  if (value === null || value === void 0) {
    return "-";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number") {
    return value.toString();
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    return value.map(String).join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};
var createMetadataColumns = (metadataFields) => {
  return Object.entries(metadataFields).map(([fieldId, label]) => ({
    accessorKey: `metadata.${fieldId}`,
    header: label,
    cell: ({ row }) => {
      const value = row.original.metadata?.[fieldId];
      return /* @__PURE__ */ jsx2("span", { className: "text-sm", children: formatMetadataValue(value) });
    },
    enableColumnFilter: true
  }));
};
var baseColumns = [
  {
    accessorKey: "job_id",
    header: "Job ID",
    cell: ({ row }) => /* @__PURE__ */ jsx2("span", { className: "font-mono text-xs", children: row.original.job_id }),
    enableColumnFilter: true
  },
  {
    accessorKey: "process_name",
    header: "Type",
    // We'll transform process_name -> readable type
    cell: ({ row }) => getJobTypeName2(row.original.process_name),
    // TanStack Table will do text sorting by default
    filterFn: (row, id, value) => {
      const processName = row.getValue(id);
      const displayName = getJobTypeName2(processName);
      return displayName.toLowerCase().includes(value.toLowerCase());
    }
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.status;
      const statusColor = {
        "PENDING": "text-yellow-600",
        "RUNNING": "text-blue-600",
        "COMPLETED": "text-green-600",
        "ERROR": "text-red-600"
      }[status] || "";
      return /* @__PURE__ */ jsx2("span", { className: `font-medium ${statusColor}`, children: status });
    }
  },
  {
    accessorKey: "created_at",
    header: "Created (ET)",
    cell: ({ row }) => formatToEastern(row.original.created_at)
  },
  {
    accessorKey: "wait_duration_ms",
    header: "Wait Time",
    cell: ({ row }) => {
      const duration = row.original.wait_duration_ms;
      if (!duration) return "-";
      const isLong = duration > 3e5;
      const style = isLong ? { color: "orange", fontWeight: "bold" } : {};
      return /* @__PURE__ */ jsxs2("span", { style, children: [
        (duration / 1e3).toFixed(1),
        "s"
      ] });
    }
  },
  {
    accessorKey: "run_duration_ms",
    header: "Run Time",
    cell: ({ row }) => {
      const duration = row.original.run_duration_ms;
      if (!duration) return "-";
      const isLong = duration > 6e5;
      const style = isLong ? { color: "red", fontWeight: "bold" } : {};
      return /* @__PURE__ */ jsxs2("span", { style, children: [
        (duration / 1e3).toFixed(1),
        "s"
      ] });
    }
  },
  {
    accessorKey: "user_id",
    header: "User"
  }
];
var createContextColumns = (showOrgId, showWsId) => {
  const contextColumns = [];
  if (showOrgId) {
    contextColumns.push({
      accessorKey: "org_id",
      header: "Org ID",
      cell: ({ row }) => /* @__PURE__ */ jsx2("span", { className: "font-mono text-xs text-muted-foreground", children: row.original.org_id || "-" }),
      enableColumnFilter: true
    });
  }
  if (showWsId) {
    contextColumns.push({
      accessorKey: "ws_id",
      header: "WS ID",
      cell: ({ row }) => /* @__PURE__ */ jsx2("span", { className: "font-mono text-xs text-muted-foreground", children: row.original.ws_id || "-" }),
      enableColumnFilter: true
    });
  }
  return contextColumns;
};
var createJobColumns = (metadataFields, context) => {
  const columns = [...baseColumns];
  const showOrgId = !context?.org_id;
  const showWsId = !context?.ws_id;
  if (showOrgId || showWsId) {
    const contextColumns = createContextColumns(showOrgId, showWsId);
    columns.splice(-1, 0, ...contextColumns);
  }
  if (metadataFields && Object.keys(metadataFields).length > 0) {
    const metadataColumns = createMetadataColumns(metadataFields);
    columns.splice(-1, 0, ...metadataColumns);
  }
  return columns;
};
var jobColumns = createJobColumns();

// src/components/jobs/RecentJobsTable.tsx
import { Alert, AlertDescription } from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx3 } from "react/jsx-runtime";
function RecentJobsTable({ month, org_id, ws_id, metadata_fields }) {
  const { data, isLoading, error } = useRecentJobs(org_id, ws_id, month);
  const columns = createJobColumns(metadata_fields, { org_id, ws_id });
  if (isLoading) {
    return /* @__PURE__ */ jsx3("div", { className: "flex items-center justify-center p-8", children: /* @__PURE__ */ jsx3("div", { className: "text-muted-foreground", children: "Loading jobs..." }) });
  }
  if (error) {
    return /* @__PURE__ */ jsx3(Alert, { variant: "destructive", children: /* @__PURE__ */ jsx3(AlertDescription, { children: "Failed to load jobs. Please try again later." }) });
  }
  if (!data?.items.length) {
    return /* @__PURE__ */ jsx3("div", { className: "text-center text-muted-foreground p-8", children: "No jobs found." });
  }
  return /* @__PURE__ */ jsx3(
    DataTable,
    {
      columns,
      data: data.items
    }
  );
}

// src/components/jobs/AuthErrorAlert.tsx
import { useState, useEffect } from "react";
import { Alert as Alert2, AlertDescription as AlertDescription2 } from "@umd-dit-ai-workspace/shared-ui";
import { AlertTriangle } from "lucide-react";

// src/api/api.ts
var authEvents = {
  listeners: /* @__PURE__ */ new Set(),
  onAuthError(callback) {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  },
  emitAuthError(event) {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error("Error in auth error listener:", error);
      }
    });
  }
};

// src/components/jobs/AuthErrorAlert.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function AuthErrorAlert() {
  const [authError, setAuthError] = useState(null);
  useEffect(() => {
    const unsubscribe = authEvents.onAuthError((event) => {
      setAuthError(event.message || "Authentication error occurred");
      setTimeout(() => {
        setAuthError(null);
      }, 5e3);
    });
    return () => {
      unsubscribe();
    };
  }, []);
  if (!authError) return null;
  return /* @__PURE__ */ jsxs3(Alert2, { variant: "destructive", className: "mb-4", children: [
    /* @__PURE__ */ jsx4(AlertTriangle, { className: "h-4 w-4" }),
    /* @__PURE__ */ jsx4(AlertDescription2, { children: authError })
  ] });
}

// src/components/jobs/JobsComponent.tsx
import { Fragment, jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function JobsComponent({
  org_id,
  ws_id,
  isAdmin = false,
  metadata_fields
}) {
  const [month, setMonth] = useState2(format(/* @__PURE__ */ new Date(), "yyyy-MM"));
  const availableTabs = [];
  if (isAdmin) {
    availableTabs.push("application");
    if (org_id) {
      availableTabs.push("organization");
    }
  }
  if (org_id && ws_id) {
    availableTabs.push("workspace");
  }
  const defaultTab = availableTabs.includes("workspace") ? "workspace" : availableTabs.includes("organization") ? "organization" : availableTabs.includes("application") ? "application" : "";
  const [activeTab, setActiveTab] = useState2(defaultTab);
  const getTabDescription = () => {
    switch (activeTab) {
      case "application":
        return `Application-level jobs`;
      case "organization":
        return `Organization-level jobs for ${org_id}`;
      case "workspace":
        return `Workspace-level jobs for ${org_id}/${ws_id}`;
      default:
        return "Monitor bill processing jobs and statistics";
    }
  };
  const renderJobContent = (tabValue) => {
    let contextOrgId;
    let contextWsId;
    switch (tabValue) {
      case "application":
        contextOrgId = void 0;
        contextWsId = void 0;
        break;
      case "organization":
        contextOrgId = org_id;
        contextWsId = void 0;
        break;
      case "workspace":
        contextOrgId = org_id;
        contextWsId = ws_id;
        break;
    }
    return /* @__PURE__ */ jsxs4(Fragment, { children: [
      /* @__PURE__ */ jsx5(
        MonthlyStatsView,
        {
          month,
          org_id: contextOrgId,
          ws_id: contextWsId
        }
      ),
      /* @__PURE__ */ jsxs4("div", { children: [
        /* @__PURE__ */ jsx5("h3", { className: "text-lg font-medium mb-4", children: "Recent Jobs" }),
        /* @__PURE__ */ jsx5(
          RecentJobsTable,
          {
            month,
            org_id: contextOrgId,
            ws_id: contextWsId,
            metadata_fields
          }
        )
      ] })
    ] });
  };
  return /* @__PURE__ */ jsx5("div", { className: "container mx-auto px-4", children: /* @__PURE__ */ jsxs4("div", { className: "space-y-6 bg-background text-foreground p-4 rounded-lg", children: [
    /* @__PURE__ */ jsxs4("div", { className: "flex justify-between items-center", children: [
      /* @__PURE__ */ jsxs4("div", { children: [
        /* @__PURE__ */ jsx5("h1", { className: "text-2xl font-bold", children: "Jobs" }),
        /* @__PURE__ */ jsx5("p", { className: "text-sm text-gray-700 dark:text-gray-300 mt-1", children: getTabDescription() })
      ] }),
      /* @__PURE__ */ jsxs4(Select, { value: month, onValueChange: setMonth, children: [
        /* @__PURE__ */ jsx5(SelectTrigger, { className: "w-[180px]", children: /* @__PURE__ */ jsx5(SelectValue, {}) }),
        /* @__PURE__ */ jsx5(SelectContent, { children: Array.from({ length: 6 }).map((_, i) => {
          const date = subMonths(/* @__PURE__ */ new Date(), i);
          const value = format(date, "yyyy-MM");
          return /* @__PURE__ */ jsx5(SelectItem, { value, children: format(date, "MMMM yyyy") }, value);
        }) })
      ] })
    ] }),
    /* @__PURE__ */ jsx5(AuthErrorAlert, {}),
    availableTabs.length > 0 ? /* @__PURE__ */ jsxs4(Tabs, { value: activeTab, onValueChange: setActiveTab, className: "w-full", children: [
      /* @__PURE__ */ jsxs4(TabsList, { className: `grid w-full ${availableTabs.length === 1 ? "grid-cols-1" : availableTabs.length === 2 ? "grid-cols-2" : "grid-cols-3"}`, children: [
        availableTabs.includes("application") && /* @__PURE__ */ jsx5(TabsTrigger, { value: "application", children: "Application" }),
        availableTabs.includes("organization") && /* @__PURE__ */ jsx5(TabsTrigger, { value: "organization", children: "Organization" }),
        availableTabs.includes("workspace") && /* @__PURE__ */ jsx5(TabsTrigger, { value: "workspace", children: "Workspace" })
      ] }),
      availableTabs.includes("application") && /* @__PURE__ */ jsx5(TabsContent, { value: "application", className: "space-y-6", children: renderJobContent("application") }),
      availableTabs.includes("organization") && /* @__PURE__ */ jsx5(TabsContent, { value: "organization", className: "space-y-6", children: renderJobContent("organization") }),
      availableTabs.includes("workspace") && /* @__PURE__ */ jsx5(TabsContent, { value: "workspace", className: "space-y-6", children: renderJobContent("workspace") })
    ] }) : /* @__PURE__ */ jsxs4("div", { className: "text-center py-8 text-gray-500", children: [
      /* @__PURE__ */ jsxs4("p", { children: [
        "No jobs available to display. ",
        !isAdmin && "Admin access required to view application-level jobs."
      ] }),
      !org_id && /* @__PURE__ */ jsx5("p", { className: "mt-2", children: "Please provide organization and workspace IDs to view workspace jobs." })
    ] })
  ] }) });
}

// src/JobsComponent.tsx
import { Fragment as Fragment2, jsx as jsx6 } from "react/jsx-runtime";
var queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});
var ApiContext = createContext(null);
var useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApi must be used within JobsComponent");
  }
  return context;
};
var AuthErrorHandler = ({ children }) => {
  return /* @__PURE__ */ jsx6(Fragment2, { children });
};
var JobsComponent2 = ({
  org_id,
  ws_id,
  isAdmin = false,
  metadata_fields,
  apiInstance
}) => {
  return /* @__PURE__ */ jsx6(QueryClientProvider, { client: queryClient, children: /* @__PURE__ */ jsx6(ApiContext.Provider, { value: { apiInstance }, children: /* @__PURE__ */ jsx6(AuthErrorHandler, { children: /* @__PURE__ */ jsx6(
    JobsComponent,
    {
      org_id,
      ws_id,
      isAdmin,
      metadata_fields
    }
  ) }) }) });
};

// src/components/JobPoller.tsx
import { useEffect as useEffect2, useRef, useCallback } from "react";
function JobPoller({
  jobId,
  jobContext,
  onStatusChange,
  onComplete,
  onError,
  pollingInterval = 2e3,
  timeout = 3e5,
  initialDelay = 3e3
}) {
  const pollingIntervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  const handleError = useCallback((error) => {
    if (onError) {
      onError(error);
    } else {
      console.error("JobPoller error:", error);
    }
  }, [onError]);
  const startPolling = useCallback(() => {
    if (!jobId) return;
    console.log(`Starting to poll for job ${jobId}`);
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const jobData = await getJobByContext(jobContext, jobId);
        console.log(`Job ${jobId} status: ${jobData.status}`);
        onStatusChange(jobData.status);
        if (jobData.status === "COMPLETED") {
          console.log(`Job ${jobId} completed`);
          stopPolling();
          await onComplete(jobData);
        } else if (jobData.status === "ERROR") {
          console.error(`Job ${jobId} failed`);
          stopPolling();
          handleError(new Error("Job failed"));
        }
      } catch (error) {
        console.error("Error polling for job status:", error);
      }
    }, pollingInterval);
    timeoutRef.current = setTimeout(() => {
      stopPolling();
      onStatusChange("TIMEOUT");
      handleError(new Error("Job polling timed out"));
    }, timeout);
  }, [jobId, jobContext, onStatusChange, onComplete, handleError, pollingInterval, timeout, stopPolling]);
  useEffect2(() => {
    if (!jobId) return;
    const delayTimeout = setTimeout(() => {
      startPolling();
    }, initialDelay);
    return () => {
      clearTimeout(delayTimeout);
      stopPolling();
    };
  }, [jobId, initialDelay, startPolling, stopPolling]);
  return null;
}

// src/components/JobStatus.tsx
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
function JobStatus({
  jobId,
  jobStatus,
  isProcessing,
  processDescription = "Processing..."
}) {
  if (!isProcessing) {
    return null;
  }
  return /* @__PURE__ */ jsxs5("div", { className: "bg-white p-8 rounded-xl shadow-lg text-center", children: [
    /* @__PURE__ */ jsx7("div", { className: "animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" }),
    /* @__PURE__ */ jsx7("p", { className: "text-lg text-gray-800 font-semibold mb-2", children: processDescription }),
    jobId && /* @__PURE__ */ jsxs5("div", { className: "text-sm text-gray-600", children: [
      /* @__PURE__ */ jsxs5("p", { children: [
        "Job ID: ",
        jobId
      ] }),
      jobStatus && /* @__PURE__ */ jsxs5("p", { className: "mt-1", children: [
        "Status: ",
        /* @__PURE__ */ jsx7("span", { className: `font-medium ${jobStatus === "RUNNING" ? "text-blue-600" : jobStatus === "PENDING" ? "text-yellow-600" : jobStatus === "COMPLETED" ? "text-green-600" : jobStatus === "ERROR" ? "text-red-600" : jobStatus === "TIMEOUT" ? "text-orange-600" : "text-gray-600"}`, children: jobStatus })
      ] })
    ] })
  ] });
}

// src/index.ts
var jobsApi = null;
function initializeJobsApi(apiInstance) {
  jobsApi = createJobsApi(apiInstance);
  console.log("[Jobs] API initialized with provided instance");
}
function getJobsApi() {
  if (!jobsApi) {
    throw new Error("Jobs API not initialized. Call initializeJobsApi(apiInstance) first.");
  }
  return jobsApi;
}
function getJob(jobId) {
  const api = getJobsApi();
  return api.getJob(jobId);
}
function getJobByContext(context, jobId) {
  const api = getJobsApi();
  return api.getJobByContext(context, jobId);
}
export {
  JobPoller,
  JobStatus,
  JobsComponent2 as JobsComponent,
  createJobsApi,
  getJob,
  getJobByContext,
  initializeJobsApi,
  useApi
};
//# sourceMappingURL=index.js.map